if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        try {
          this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
          this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
          this.descriptionContent = this.querySelector('[data-description-content]');
          this.descriptionToggle = this.querySelector('[data-description-toggle]');
          if (!this.tabs.length) return;
          this.onClick = this.onClick.bind(this);
          this.onKeydown = this.onKeydown.bind(this);
          this.onHashChange = this.onHashChange.bind(this);
          this.onDescriptionToggle = this.onDescriptionToggle.bind(this);
          this.tabs.forEach((tab) => {
            tab.addEventListener('click', this.onClick);
            tab.addEventListener('keydown', this.onKeydown);
          });
          this.descriptionToggle?.addEventListener('click', this.onDescriptionToggle);
          window.addEventListener('hashchange', this.onHashChange);
          this.activateFromHash(window.location.hash.replace('#', ''));
          // Wait for fonts so scrollHeight measurement is accurate
          (document.fonts?.ready ?? Promise.resolve()).then(() => {
            requestAnimationFrame(() => this.syncDescriptionClamp());
          });
        } catch (error) {
          console.error(error);
        }
      }

      disconnectedCallback() {
        this.tabs?.forEach((tab) => {
          tab.removeEventListener('click', this.onClick);
          tab.removeEventListener('keydown', this.onKeydown);
        });
        this.descriptionToggle?.removeEventListener('click', this.onDescriptionToggle);
        window.removeEventListener('hashchange', this.onHashChange);
      }

      onClick(event) {
        this.activateTab(event.currentTarget, true);
      }

      onKeydown(event) {
        const currentIndex = this.tabs.indexOf(event.currentTarget);
        if (currentIndex === -1) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextTab = this.tabs[(currentIndex + direction + this.tabs.length) % this.tabs.length];
          nextTab.focus();
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.activateTab(event.currentTarget, true);
        }
      }

      onHashChange() {
        this.activateFromHash(window.location.hash.replace('#', ''));
      }

      activateFromHash(hash) {
        const tab = this.tabs.find((item) => item.dataset.hash === hash) || this.tabs[0];
        this.activateTab(tab, false);
      }

      activateTab(tab, updateHistory) {
        if (!tab) return;
        this.tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          const panel = this.querySelector(`#${item.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !selected;
        });
        if (updateHistory && tab.dataset.hash) {
          window.history.pushState({}, '', `#${tab.dataset.hash}`);
        }
        requestAnimationFrame(() => this.syncDescriptionClamp());
      }

      onDescriptionToggle() {
        const expanded = this.descriptionContent?.classList.toggle('is-expanded');
        if (!this.descriptionToggle) return;
        this.descriptionToggle.setAttribute('aria-expanded', String(Boolean(expanded)));
        this.descriptionToggle.textContent = expanded
          ? this.descriptionToggle.dataset.collapseLabel || this.descriptionToggle.textContent
          : this.descriptionToggle.dataset.expandLabel || this.descriptionToggle.textContent;
        this.descriptionContent?.classList.toggle('is-clamped', !expanded);
      }

      syncDescriptionClamp() {
        if (!this.descriptionContent || !this.descriptionToggle) return;
        const lineHeight = Number.parseFloat(window.getComputedStyle(this.descriptionContent).lineHeight || '0');
        const clampHeight = lineHeight ? lineHeight * 10 : 0;
        const shouldClamp = clampHeight ? this.descriptionContent.scrollHeight > clampHeight + 8 : false;
        this.descriptionContent.classList.toggle('is-clamped', shouldClamp && !this.descriptionContent.classList.contains('is-expanded'));
        this.descriptionToggle.hidden = !shouldClamp;
        this.descriptionToggle.textContent = this.descriptionToggle.dataset.expandLabel || this.descriptionToggle.textContent;
        this.descriptionToggle.setAttribute('aria-expanded', 'false');
      }
    }
  );
}
